import React, { useState, useEffect } from 'react';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';

interface EditPatientModalProps {
    isOpen: boolean;
    onClose: () => void;
    patient: any;
    onSave: (updatedData: any) => Promise<void>;
    isSaving?: boolean;
    lang?: string;
}

export const EditPatientModal: React.FC<EditPatientModalProps> = ({
    isOpen,
    onClose,
    patient,
    onSave,
    isSaving = false,
    lang,
}) => {
    // Text direction based on clinic language
    const textDirection = lang === "ENG" ? "ltr" : "rtl";

    const isYouth = patient?.patient_type === 'Kids' || patient?.patient_type === 'Youth';

    // Youth/Adult patient own data state
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [patientId, setPatientId] = useState('');
    const [gender, setGender] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [dob, setDob] = useState('');
    const [children, setChildren] = useState('');

    // Parent 1 state
    const [parent1Name, setParent1Name] = useState('');
    const [parent1Gender, setParent1Gender] = useState('');
    const [parent1Age, setParent1Age] = useState(0);
    const [parent1Email, setParent1Email] = useState('');
    const [parent1Phone, setParent1Phone] = useState('');
    const [parent1Id, setParent1Id] = useState('');

    // Parent 2 state
    const [parent2Name, setParent2Name] = useState('');
    const [parent2Gender, setParent2Gender] = useState('');
    const [parent2Age, setParent2Age] = useState(0);
    const [parent2Email, setParent2Email] = useState('');
    const [parent2Phone, setParent2Phone] = useState('');
    const [parent2Id, setParent2Id] = useState('');

    // Youth-only state
    const [parentsMaritalStatus, setParentsMaritalStatus] = useState('');

    const [error, setError] = useState<string | null>(null);
    const [emailErrors, setEmailErrors] = useState<{ [key: string]: string }>({});
    const [phoneErrors, setPhoneErrors] = useState<{ [key: string]: string }>({});
    const [idErrors, setIdErrors] = useState<{ [key: string]: string }>({});
    const [dobError, setDobError] = useState<string | null>(null);
    const [touchedIdFields, setTouchedIdFields] = useState<Set<string>>(new Set());

    // Tab state for youth form
    const [activeTab, setActiveTab] = useState<'patient' | 'parent1' | 'parent2'>('patient');

    // Email validation
    const validateEmail = (email: string): boolean => {
        if (!email) return true; // Empty is valid (optional field)
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Phone validation - digits only, optional + at beginning
    const validatePhone = (phone: string): boolean => {
        if (!phone) return true; // Empty is valid (optional field)
        const phoneRegex = /^\+?\d+$/;
        return phoneRegex.test(phone);
    };

    // ID validation - digits only, exactly 9 characters
    const getIdError = (id: string): string | null => {
        if (!id) return 'ID must be exactly 9 digits';
        if (!/^\d+$/.test(id)) return 'ID must contain only digits';
        if (id.length !== 9) return 'ID must be exactly 9 digits';
        return null;
    };

    // DOB validation - required, parseable, not in the future
    const getDobError = (value: string): string | null => {
        if (!value) return 'DOB is required';
        const parsed = new Date(value);
        if (isNaN(parsed.getTime())) return 'Invalid date';
        const today = new Date();
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        if (value > todayStr) return 'DOB cannot be in the future';
        return null;
    };

    const handleDobChange = (value: string) => {
        setDob(value);
        setDobError(getDobError(value));
    };

    const handleEmailChange = (field: string, value: string, setter: (v: string) => void) => {
        setter(value);
        if (value && !validateEmail(value)) {
            setEmailErrors(prev => ({ ...prev, [field]: 'Invalid email format' }));
        } else {
            setEmailErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handlePhoneChange = (field: string, value: string, setter: (v: string) => void) => {
        setter(value);
        if (value && !validatePhone(value)) {
            setPhoneErrors(prev => ({ ...prev, [field]: 'Phone must contain only digits (optional + at start)' }));
        } else {
            setPhoneErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleIdChange = (field: string, value: string, setter: (v: string) => void) => {
        setter(value);
        setTouchedIdFields(prev => new Set(prev).add(field));
        const error = getIdError(value);
        if (error) {
            setIdErrors(prev => ({ ...prev, [field]: error }));
        } else {
            setIdErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    // Initialize form data when patient changes
    useEffect(() => {
        setIdErrors({});
        setTouchedIdFields(new Set());
        if (patient) {
            // Load patient's own data (for both youth and adult)
            setFirstName(patient.first_name || '');
            setLastName(patient.last_name || '');
            setPatientId(patient.patient_id || patient.HM_ID || '');
            setGender(patient.gender || '');
            setPhone(patient.phone || patient.main_phone || '');
            setEmail(patient.email || '');
            setDob(patient.DOB || '');
            setDobError(getDobError(patient.DOB || ''));
            setChildren(patient.children !== undefined && patient.children !== null ? String(patient.children) : '');

            if (isYouth) {
                // Also load parent data for youth
                setParent1Name(patient.parent_1_name || '');
                setParent1Gender(patient.parent_1_gender || '');
                setParent1Age(patient.parent_1_age || 0);
                setParent1Email(patient.parent_1_email || '');
                setParent1Phone(patient.parent_1_phone_number || '');
                setParent1Id(patient.parent_1_id || '');

                setParent2Name(patient.parent_2_name || '');
                setParent2Gender(patient.parent_2_gender || '');
                setParent2Age(patient.parent_2_age || 0);
                setParent2Email(patient.parent_2_email || '');
                setParent2Phone(patient.parent_2_phone_number || '');
                setParent2Id(patient.parent_2_id || '');
                setParentsMaritalStatus(patient.parents_marital_status || '');
            }
        }
    }, [patient, isYouth, isOpen]);

    if (!isOpen || !patient) return null;

    const handleSave = async () => {
        try {
            setError(null);

            // Validate touched ID fields on save
            const newIdErrors: { [key: string]: string } = {};
            const idFieldValues: { [key: string]: string } = isYouth
                ? { patient_id_youth: patientId, parent_1_id: parent1Id, parent_2_id: parent2Id }
                : { patient_id: patientId };
            for (const field of touchedIdFields) {
                if (field in idFieldValues) {
                    const err = getIdError(idFieldValues[field]);
                    if (err) newIdErrors[field] = err;
                }
            }
            if (Object.keys(newIdErrors).length > 0) {
                setIdErrors(newIdErrors);
                setError('Please fix validation errors before saving');
                return;
            }

            // Validate DOB on save
            const dobErr = getDobError(dob);
            if (dobErr) {
                setDobError(dobErr);
                setError('Fix invalid DOB');
                return;
            }

            // Check for other validation errors
            if (Object.keys(emailErrors).length > 0 || Object.keys(phoneErrors).length > 0 || Object.keys(idErrors).length > 0) {
                setError('Please fix validation errors before saving');
                return;
            }

            const updatedData: any = isYouth ? {
                // Youth patient's own data
                first_name: firstName,
                last_name: lastName,
                patient_id: patientId,
                gender: gender,
                phone: phone,
                DOB: dob,
                children: Number(children) || 0,
                // Parent data
                parent_1_name: parent1Name,
                parent_1_gender: parent1Gender,
                parent_1_age: parent1Age,
                parent_1_email: parent1Email,
                parent_1_phone_number: parent1Phone,
                parent_1_id: parent1Id,
                parent_2_name: parent2Name,
                parent_2_gender: parent2Gender,
                parent_2_age: parent2Age,
                parent_2_email: parent2Email,
                parent_2_phone_number: parent2Phone,
                parent_2_id: parent2Id,
                parents_marital_status: parentsMaritalStatus,
            } : {
                first_name: firstName,
                last_name: lastName,
                patient_id: patientId,
                gender: gender,
                phone: phone,
                email: email,
                DOB: dob,
                children: Number(children) || 0,
            };

            await onSave(updatedData);
        } catch (err: any) {
            setError(err.message || 'Failed to save patient information');
        }
    };

    const handleCancel = () => {
        setError(null);
        setIdErrors({});
        setDobError(null);
        setTouchedIdFields(new Set());
        onClose();
    };

    const renderAdultForm = () => (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">First Name</label>
                    <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        dir={textDirection}
                        className="flex-1 px-3 py-1.5 rounded-lg border border-[#B4D9D4] bg-[#EBF5F5] focus:outline-none focus:ring-2 focus:ring-[#366A75]"
                        disabled={isSaving}
                    />
                </div>
                <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">Last Name</label>
                    <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        dir={textDirection}
                        className="flex-1 px-3 py-1.5 rounded-lg border border-[#B4D9D4] bg-[#EBF5F5] focus:outline-none focus:ring-2 focus:ring-[#366A75]"
                        disabled={isSaving}
                    />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">ID</label>
                    <div className="flex-1">
                        <input
                            type="text"
                            value={patientId}
                            onChange={(e) => handleIdChange('patient_id', e.target.value, setPatientId)}
                            dir={textDirection}
                            className={`w-full px-3 py-1.5 rounded-lg border ${idErrors.patient_id ? 'border-red-500' : 'border-[#B4D9D4]'} bg-[#EBF5F5] focus:outline-none focus:ring-2 focus:ring-[#366A75]`}
                            disabled={isSaving}
                        />
                        {idErrors.patient_id && <p className="text-red-500 text-xs mt-0.5">{idErrors.patient_id}</p>}
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">Gender</label>
                    <select
                        value={gender}
                        onChange={(e) => setGender(e.target.value)}
                        className="flex-1 px-3 py-1.5 rounded-lg border border-[#B4D9D4] bg-[#EBF5F5] focus:outline-none focus:ring-2 focus:ring-[#366A75]"
                        disabled={isSaving}
                    >
                        <option value="">Select an option</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">DOB</label>
                    <div className="flex-1">
                        <input
                            type="date"
                            value={dob}
                            onChange={(e) => handleDobChange(e.target.value)}
                            className={`w-full px-3 py-1.5 rounded-lg border ${dobError ? 'border-red-500' : 'border-[#B4D9D4]'} bg-[#EBF5F5] focus:outline-none focus:ring-2 focus:ring-[#366A75]`}
                            disabled={isSaving}
                        />
                        {dobError && <p className="text-red-500 text-xs mt-0.5">{dobError}</p>}
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">Children</label>
                    <input
                        type="text"
                        inputMode="numeric"
                        value={children}
                        onChange={(e) => setChildren(e.target.value.replace(/[^0-9]/g, ''))}
                        className="flex-1 px-3 py-1.5 rounded-lg border border-[#B4D9D4] bg-[#EBF5F5] focus:outline-none focus:ring-2 focus:ring-[#366A75]"
                        disabled={isSaving}
                    />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">Phone</label>
                    <div className="flex-1 flex items-center gap-2">
                        <span className="text-gray-500">📞</span>
                        <div className="flex-1">
                            <input
                                type="tel"
                                value={phone}
                                onChange={(e) => handlePhoneChange('phone', e.target.value, setPhone)}
                                dir={textDirection}
                                className={`w-full px-3 py-1.5 rounded-lg border ${phoneErrors.phone ? 'border-red-500' : 'border-[#B4D9D4]'} bg-[#EBF5F5] focus:outline-none focus:ring-2 focus:ring-[#366A75]`}
                                disabled={isSaving}
                            />
                            {phoneErrors.phone && <p className="text-red-500 text-xs mt-0.5">{phoneErrors.phone}</p>}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">Email</label>
                    <div className="flex-1 flex items-center gap-2">
                        <span className="text-gray-500">📧</span>
                        <div className="flex-1">
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => handleEmailChange('email', e.target.value, setEmail)}
                                dir={textDirection}
                                className={`w-full px-3 py-1.5 rounded-lg border ${emailErrors.email ? 'border-red-500' : 'border-[#B4D9D4]'} bg-[#EBF5F5] focus:outline-none focus:ring-2 focus:ring-[#366A75]`}
                                disabled={isSaving}
                            />
                            {emailErrors.email && <p className="text-red-500 text-xs mt-0.5">{emailErrors.email}</p>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderParentSection = (
        title: string,
        name: string,
        setName: (v: string) => void,
        genderVal: string,
        setGenderVal: (v: string) => void,
        age: number,
        setAge: (v: number) => void,
        emailVal: string,
        setEmailVal: (v: string) => void,
        phoneVal: string,
        setPhoneVal: (v: string) => void,
        idVal: string,
        setIdVal: (v: string) => void,
        isCollapsible: boolean = false
    ) => {
        const content = (
            <div className="space-y-2">
                <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-20 flex-shrink-0">Name</label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Enter value"
                        dir={textDirection}
                        className="flex-1 px-3 py-1.5 rounded-lg border border-[#B4D9D4] bg-[#EBF5F5] focus:outline-none focus:ring-2 focus:ring-[#366A75]"
                        disabled={isSaving}
                    />
                </div>

                <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-20 flex-shrink-0">Gender</label>
                    <select
                        value={genderVal}
                        onChange={(e) => setGenderVal(e.target.value)}
                        className="flex-1 px-3 py-1.5 rounded-lg border border-[#B4D9D4] bg-[#EBF5F5] focus:outline-none focus:ring-2 focus:ring-[#366A75]"
                        disabled={isSaving}
                    >
                        <option value="">Select an option</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                    </select>
                </div>

                <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-20 flex-shrink-0">Age</label>
                    <input
                        type="number"
                        value={age}
                        onChange={(e) => setAge(parseInt(e.target.value) || 0)}
                        className="flex-1 px-3 py-1.5 rounded-lg border border-[#B4D9D4] bg-[#EBF5F5] focus:outline-none focus:ring-2 focus:ring-[#366A75]"
                        disabled={isSaving}
                    />
                </div>

                <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-20 flex-shrink-0">Email</label>
                    <div className="flex-1 flex items-center gap-2">
                        <span className="text-gray-500">📧</span>
                        <div className="flex-1">
                            <input
                                type="email"
                                value={emailVal}
                                onChange={(e) => handleEmailChange(`parent_${title.includes('1') ? '1' : '2'}_email`, e.target.value, setEmailVal)}
                                dir={textDirection}
                                className={`w-full px-3 py-1.5 rounded-lg border ${emailErrors[`parent_${title.includes('1') ? '1' : '2'}_email`] ? 'border-red-500' : 'border-[#B4D9D4]'} bg-[#EBF5F5] focus:outline-none focus:ring-2 focus:ring-[#366A75]`}
                                disabled={isSaving}
                            />
                            {emailErrors[`parent_${title.includes('1') ? '1' : '2'}_email`] && <p className="text-red-500 text-xs mt-0.5">{emailErrors[`parent_${title.includes('1') ? '1' : '2'}_email`]}</p>}
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-20 flex-shrink-0">Phone</label>
                    <div className="flex-1 flex items-center gap-2">
                        <span className="text-gray-500">📞</span>
                        <div className="flex-1">
                            <input
                                type="tel"
                                value={phoneVal}
                                onChange={(e) => handlePhoneChange(`parent_${title.includes('1') ? '1' : '2'}_phone`, e.target.value, setPhoneVal)}
                                dir={textDirection}
                                className={`w-full px-3 py-1.5 rounded-lg border ${phoneErrors[`parent_${title.includes('1') ? '1' : '2'}_phone`] ? 'border-red-500' : 'border-[#B4D9D4]'} bg-[#EBF5F5] focus:outline-none focus:ring-2 focus:ring-[#366A75]`}
                                disabled={isSaving}
                            />
                            {phoneErrors[`parent_${title.includes('1') ? '1' : '2'}_phone`] && <p className="text-red-500 text-xs mt-0.5">{phoneErrors[`parent_${title.includes('1') ? '1' : '2'}_phone`]}</p>}
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-20 flex-shrink-0">ID</label>
                    <div className="flex-1">
                        <input
                            type="text"
                            value={idVal}
                            onChange={(e) => handleIdChange(`parent_${title.includes('1') ? '1' : '2'}_id`, e.target.value, setIdVal)}
                            dir={textDirection}
                            className={`w-full px-3 py-1.5 rounded-lg border ${idErrors[`parent_${title.includes('1') ? '1' : '2'}_id`] ? 'border-red-500' : 'border-[#B4D9D4]'} bg-[#EBF5F5] focus:outline-none focus:ring-2 focus:ring-[#366A75]`}
                            disabled={isSaving}
                        />
                        {idErrors[`parent_${title.includes('1') ? '1' : '2'}_id`] && <p className="text-red-500 text-xs mt-0.5">{idErrors[`parent_${title.includes('1') ? '1' : '2'}_id`]}</p>}
                    </div>
                </div>
            </div>
        );

        // If used in tabs, don't wrap with container
        if (!isCollapsible) {
            return content;
        }

        // Otherwise, wrap with container and header
        return (
            <div className="flex-1 bg-white rounded-lg p-4 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                    <button className="text-gray-400 hover:text-gray-600">
                        <span className="text-sm">▲</span>
                    </button>
                </div>
                {content}
            </div>
        );
    };

    const renderYouthForm = () => (
        <div className="space-y-4">
            {/* Tab Navigation */}
            <div className="flex gap-2 border-b border-gray-300">
                <button
                    onClick={() => setActiveTab('patient')}
                    className={`px-4 py-2 font-medium transition-colors ${activeTab === 'patient'
                        ? 'text-[#4A6B7C] border-b-2 border-[#4A6B7C]'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                    type="button"
                >
                    Patient
                </button>
                <button
                    onClick={() => setActiveTab('parent1')}
                    className={`px-4 py-2 font-medium transition-colors ${activeTab === 'parent1'
                        ? 'text-[#4A6B7C] border-b-2 border-[#4A6B7C]'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                    type="button"
                >
                    Parent 1
                </button>
                <button
                    onClick={() => setActiveTab('parent2')}
                    className={`px-4 py-2 font-medium transition-colors ${activeTab === 'parent2'
                        ? 'text-[#4A6B7C] border-b-2 border-[#4A6B7C]'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                    type="button"
                >
                    Parent 2
                </button>
            </div>

            {/* Tab Content */}
            <div className="bg-white rounded-lg p-4 border border-gray-200 min-h-[400px]">
                {activeTab === 'patient' && (
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Patient Information</h3>
                        <div className="space-y-2">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex items-center gap-3">
                                    <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">First Name</label>
                                    <input
                                        type="text"
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        className="flex-1 px-3 py-1.5 rounded-lg border border-[#B4D9D4] bg-[#EBF5F5] focus:outline-none focus:ring-2 focus:ring-[#366A75]"
                                        disabled={isSaving}
                                    />
                                </div>
                                <div className="flex items-center gap-3">
                                    <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">Last Name</label>
                                    <input
                                        type="text"
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                        className="flex-1 px-3 py-1.5 rounded-lg border border-[#B4D9D4] bg-[#EBF5F5] focus:outline-none focus:ring-2 focus:ring-[#366A75]"
                                        disabled={isSaving}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex items-center gap-3">
                                    <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">ID</label>
                                    <div className="flex-1">
                                        <input
                                            type="text"
                                            value={patientId}
                                            onChange={(e) => handleIdChange('patient_id_youth', e.target.value, setPatientId)}
                                            className={`w-full px-3 py-1.5 rounded-lg border ${idErrors.patient_id_youth ? 'border-red-500' : 'border-[#B4D9D4]'} bg-[#EBF5F5] focus:outline-none focus:ring-2 focus:ring-[#366A75]`}
                                            disabled={isSaving}
                                        />
                                        {idErrors.patient_id_youth && <p className="text-red-500 text-xs mt-0.5">{idErrors.patient_id_youth}</p>}
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">Gender</label>
                                    <select
                                        value={gender}
                                        onChange={(e) => setGender(e.target.value)}
                                        className="flex-1 px-3 py-1.5 rounded-lg border border-[#B4D9D4] bg-[#EBF5F5] focus:outline-none focus:ring-2 focus:ring-[#366A75]"
                                        disabled={isSaving}
                                    >
                                        <option value="">Select an option</option>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex items-center gap-3">
                                    <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">DOB</label>
                                    <div className="flex-1">
                                        <input
                                            type="date"
                                            value={dob}
                                            onChange={(e) => handleDobChange(e.target.value)}
                                            className={`w-full px-3 py-1.5 rounded-lg border ${dobError ? 'border-red-500' : 'border-[#B4D9D4]'} bg-[#EBF5F5] focus:outline-none focus:ring-2 focus:ring-[#366A75]`}
                                            disabled={isSaving}
                                        />
                                        {dobError && <p className="text-red-500 text-xs mt-0.5">{dobError}</p>}
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">Children</label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        value={children}
                                        onChange={(e) => setChildren(e.target.value.replace(/[^0-9]/g, ''))}
                                        className="flex-1 px-3 py-1.5 rounded-lg border border-[#B4D9D4] bg-[#EBF5F5] focus:outline-none focus:ring-2 focus:ring-[#366A75]"
                                        disabled={isSaving}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex items-center gap-3">
                                    <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">Phone</label>
                                    <div className="flex-1 flex items-center gap-2">
                                        <span className="text-gray-500">📞</span>
                                        <div className="flex-1">
                                            <input
                                                type="tel"
                                                value={phone}
                                                onChange={(e) => handlePhoneChange('phone_youth', e.target.value, setPhone)}
                                                className={`w-full px-3 py-1.5 rounded-lg border ${phoneErrors.phone_youth ? 'border-red-500' : 'border-[#B4D9D4]'} bg-[#EBF5F5] focus:outline-none focus:ring-2 focus:ring-[#366A75]`}
                                                disabled={isSaving}
                                            />
                                            {phoneErrors.phone_youth && <p className="text-red-500 text-xs mt-0.5">{phoneErrors.phone_youth}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">Parents Marital Status</label>
                                    <select
                                        value={parentsMaritalStatus}
                                        onChange={(e) => setParentsMaritalStatus(e.target.value)}
                                        className="flex-1 px-3 py-1.5 rounded-lg border border-[#B4D9D4] bg-[#EBF5F5] focus:outline-none focus:ring-2 focus:ring-[#366A75]"
                                        disabled={isSaving}
                                        dir="rtl"
                                    >
                                        <option value="">Select an option</option>
                                        <option value="נשואים">נשואים</option>
                                        <option value="גרושים">גרושים</option>
                                        <option value="אלמן/ה">אלמן/ה</option>
                                        <option value="ידועים בציבור">ידועים בציבור</option>
                                        <option value="הורה יחידני">הורה יחידני</option>
                                        <option value="הורות משותפת">הורות משותפת</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'parent1' && (
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Parent 1</h3>
                        {renderParentSection(
                            'Parent 1:',
                            parent1Name, setParent1Name,
                            parent1Gender, setParent1Gender,
                            parent1Age, setParent1Age,
                            parent1Email, setParent1Email,
                            parent1Phone, setParent1Phone,
                            parent1Id, setParent1Id,
                            false
                        )}
                    </div>
                )}

                {activeTab === 'parent2' && (
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Parent 2</h3>
                        {renderParentSection(
                            'Parent 2:',
                            parent2Name, setParent2Name,
                            parent2Gender, setParent2Gender,
                            parent2Age, setParent2Age,
                            parent2Email, setParent2Email,
                            parent2Phone, setParent2Phone,
                            parent2Id, setParent2Id,
                            false
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <ModalWrapper modalId="EditPatientModal"
            isOpen={isOpen}
            onClose={handleCancel}
            title={`Edit ${isYouth ? 'Youth Patient' : 'Patient'}`}
            isLoading={isSaving}
            width="max-w-5xl"
        >
            {/* Form Content */}
            <div className="mb-6">
                {isYouth ? renderYouthForm() : renderAdultForm()}
            </div>

            {/* Error Message */}
            {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                    {error}
                </div>
            )}

            {/* Buttons */}
            <div className="flex justify-center gap-4">
                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className={POPUP_STYLES.button}
                >
                    {isSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                    onClick={handleCancel}
                    disabled={isSaving}
                    className={POPUP_STYLES.button}
                >
                    Cancel
                </button>
            </div>
        </ModalWrapper>
    );
};
